import {
  CaretDownIcon,
  LightningIcon,
  LightningSlashIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import * as Collapsible from '@radix-ui/react-collapsible'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import React, { useCallback, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import { forbiddenOverride, type ToastOverride, useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  useAdminRole,
  useDeleteWorkflow,
  useGetAllScalesPages,
  useGetWorkflows,
  useIsPutUpdateWorkflowMutating,
  useOnDemotion,
  usePostCreateWorkflow,
  usePutUpdateWorkflow,
} from '../../utils/api-client/api-hooks.ts'
import {
  Repository,
  Scale,
  type UpdateWorkflowRequest,
  type WorkflowResponse,
} from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import WarningIcon from '../icons/warning-icon-trace.svg?react'
import { PermissionNotice } from '../permission-notice'
import { Spinner } from '../spinner.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { TooltipWithHover } from '../tooltip'
import * as styles from './add-findings-drawer.css'
import {
  AbandonWorkflowDialog,
  CreateWorkflowButton,
  CreateWorkflowForm,
  getDefaultWorkflowName,
  ManageWorkflowsFooterButton,
  toCreateWorkflowRequest,
  useCreateWorkflowForm,
  type WorkflowParams,
} from './create-workflow-form.tsx'
import { EditWorkflowForm, EditWorkflowSaveButton, useEditWorkflowForm } from './edit-workflow-form.tsx'
import * as footerStyles from './floating-footer.css.ts'
import { FloatingFooter } from './floating-footer.tsx'
import * as localStyles from './workflow-drawer.css'
import * as sharedStyles from './workflow-shared.css'

export const DrawerContentSchema = z.enum(['create', 'manage', 'edit'])
export type DrawerContent = z.infer<typeof DrawerContentSchema>

const WORKFLOW_GROUP_HEADINGS = {
  schedule: 'Scheduled fixes',
  'new-scan': 'New scans',
  'pull-request-scan': 'Pull requests',
} as const satisfies Record<WorkflowResponse['event']['type'], string>

const WORKFLOW_GROUP_ORDER = [
  'schedule',
  'new-scan',
  'pull-request-scan',
] as const satisfies readonly WorkflowResponse['event']['type'][]

// Every workflow write 403s for a non-admin, so each one names the permission rather than falling
// through to the generic failure toast.
const CREATE_WORKFLOW_ERROR_OVERRIDES = {
  403: forbiddenOverride(<>You do not have permission to create workflows.</>),
}
const DELETE_WORKFLOW_ERROR_OVERRIDES = {
  403: forbiddenOverride(<>You do not have permission to delete workflows.</>),
}
const UPDATE_WORKFLOW_ERROR_OVERRIDES = {
  403: forbiddenOverride(<>You do not have permission to edit workflows.</>),
  409: {
    message: <>This workflow was changed elsewhere. Please close and reopen it to load the latest version.</>,
    transience: 'sticky',
  },
} satisfies Record<number, ToastOverride>

type CreateWorkflowDrawerProps = {
  repositoryType: Repository['type']
  repositoryDisplayName: string
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onSetDrawerContent: (content: DrawerContent) => unknown
  onCloseDrawer: () => unknown
  onCreateWorkflow: (params: WorkflowParams) => unknown
}

const CreateWorkflowDrawer: React.FC<CreateWorkflowDrawerProps> = ({
  repositoryType,
  repositoryDisplayName,
  hasWriteAccess,
  onSetDrawerContent,
  onCloseDrawer,
  onCreateWorkflow,
}) => {
  const [isManageWorkflowsOpen, setIsManageWorkflowsOpen] = useState(false)
  const drawerContentRef = useRef<HTMLDivElement>(null)

  const scaleResults = useGetAllScalesPages()
  const scaleItems = scaleResults.flatMap(result => result.data?._embedded.items ?? [])

  const cvssScale = scaleItems.find(scale => scale.id === 'cvss')
  const cvssMin = cvssScale?.type === 'score' ? (cvssScale.min ?? 0) : 0
  const cvssMax = cvssScale?.type === 'score' ? (cvssScale.max ?? 10) : 10

  const form = useCreateWorkflowForm({ selectedRange: [cvssMin, cvssMax] })

  const workflowName = useSelector(form.store, state => state.values.workflowName)
  const selectedTool = useSelector(form.store, state => state.values.selectedTool)
  const selectedCadence = useSelector(form.store, state => state.values.selectedCadence)
  const displayName = workflowName || getDefaultWorkflowName(selectedTool, selectedCadence) || 'Unnamed'

  return (
    <form.AppForm>
      <Dialog.Portal>
        <Dialog.Content
          ref={drawerContentRef}
          className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
          data-has-floating-footer={true}
          onInteractOutside={event => event.preventDefault()}
        >
          <ErrorBoundary fallbackTitle="Failed to load workflow">
            <CreateWorkflowView
              repositoryType={repositoryType}
              repositoryDisplayName={repositoryDisplayName}
              scaleItems={scaleItems}
            />
          </ErrorBoundary>
        </Dialog.Content>
        <FloatingFooter drawerContentRef={drawerContentRef}>
          <CreateWorkflowFooter
            scaleItems={scaleItems}
            hasWriteAccess={hasWriteAccess}
            onManageWorkflowsDirty={() => setIsManageWorkflowsOpen(true)}
            onManageWorkflowsClean={() => onSetDrawerContent('manage')}
            onCancel={onCloseDrawer}
            onCreateWorkflow={onCreateWorkflow}
          />
        </FloatingFooter>
      </Dialog.Portal>

      <AbandonWorkflowDialog
        isOpen={isManageWorkflowsOpen}
        onOpenChange={setIsManageWorkflowsOpen}
        onAbandon={() => {
          form.reset()
          onSetDrawerContent('manage')
        }}
        repositoryType={repositoryType}
        repositoryDisplayName={repositoryDisplayName}
        displayName={displayName}
      />
    </form.AppForm>
  )
}

type EditWorkflowDrawerProps = {
  workflow: WorkflowResponse
  repositoryType: Repository['type']
  repositoryDisplayName: string
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onSetDrawerContent: (content: DrawerContent) => unknown
  onUpdateWorkflow: (workflowId: string, update: UpdateWorkflowRequest) => unknown
  onRequestDelete: () => unknown
}

const EditWorkflowDrawer: React.FC<EditWorkflowDrawerProps> = ({
  workflow,
  repositoryType,
  repositoryDisplayName,
  hasWriteAccess,
  onSetDrawerContent,
  onUpdateWorkflow,
  onRequestDelete,
}) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const isUpdating = useIsPutUpdateWorkflowMutating()

  const scaleResults = useGetAllScalesPages()
  const scaleItems = scaleResults.flatMap(result => result.data?._embedded.items ?? [])

  const form = useEditWorkflowForm(workflow)

  return (
    <form.AppForm>
      <Dialog.Portal>
        <Dialog.Content
          ref={drawerContentRef}
          className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
          data-has-floating-footer={true}
          onInteractOutside={event => event.preventDefault()}
        >
          <ErrorBoundary fallbackTitle="Failed to load workflow">
            <EditWorkflowForm
              workflow={workflow}
              repositoryType={repositoryType}
              repositoryDisplayName={repositoryDisplayName}
              scaleItems={scaleItems}
            />
          </ErrorBoundary>
        </Dialog.Content>
        <FloatingFooter drawerContentRef={drawerContentRef} className={localStyles.editFloatingFooter}>
          <EditWorkflowFooter
            workflow={workflow}
            isUpdating={isUpdating}
            hasWriteAccess={hasWriteAccess}
            onDelete={onRequestDelete}
            onCancel={() => onSetDrawerContent('manage')}
            onSave={update => onUpdateWorkflow(workflow.id, update)}
          />
        </FloatingFooter>
      </Dialog.Portal>
    </form.AppForm>
  )
}

type ManageWorkflowDrawerProps = {
  repositoryId: string
  repositoryType: Repository['type']
  repositoryDisplayName: string
  /** `undefined` until the role is known — see `useIsAdmin`. */
  isAdmin: boolean | undefined
  isRoleUnavailable: boolean
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onClose: () => unknown
  onCreateNew: () => unknown
  onEditWorkflow: (workflow: WorkflowResponse) => unknown
}

const ManageWorkflowDrawer: React.FC<ManageWorkflowDrawerProps> = ({
  repositoryId,
  repositoryType,
  repositoryDisplayName,
  isAdmin,
  isRoleUnavailable,
  hasWriteAccess,
  onClose,
  onCreateNew,
  onEditWorkflow,
}) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const getWorkflowsQueryResult = useGetWorkflows({ repositoryId })
  const workflows = getWorkflowsQueryResult.data?._embedded.items ?? []
  const isWorkflowsLoading = getWorkflowsQueryResult.isLoading
  return (
    <Dialog.Portal>
      <Dialog.Content
        ref={drawerContentRef}
        className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
        onInteractOutside={event => event.preventDefault()}
      >
        <ErrorBoundary fallbackTitle="Failed to load workflow">
          <ManageWorkflowsView
            repositoryType={repositoryType}
            repositoryDisplayName={repositoryDisplayName}
            workflows={workflows}
            isWorkflowsLoading={isWorkflowsLoading}
            isAdmin={isAdmin}
            isRoleUnavailable={isRoleUnavailable}
            hasWriteAccess={hasWriteAccess}
            onClose={onClose}
            onCreateNew={onCreateNew}
            onEditWorkflow={onEditWorkflow}
          />
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog.Portal>
  )
}

export type WorkflowDrawerProps = {
  repositoryId: string
  repositoryDisplayName: string
  repositoryType: Repository['type']
  onCloseDrawer: () => unknown
  drawerContent: DrawerContent
  onSetDrawerContent: (content: DrawerContent) => unknown
}

export const WorkflowDrawer: React.FC<WorkflowDrawerProps> = ({
  repositoryId,
  repositoryDisplayName,
  repositoryType,
  onCloseDrawer,
  drawerContent,
  onSetDrawerContent,
}) => {
  const { addToast } = useToastService()
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowResponse>()
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowResponse>()
  const { isAdmin, isRoleUnavailable, hasWriteAccess } = useAdminRole()

  const showCreateWorkflowError = useShowMutationError({
    prefix: 'Failed to create workflow',
    overrides: CREATE_WORKFLOW_ERROR_OVERRIDES,
  })
  const createWorkflowMutation = usePostCreateWorkflow({
    onSuccess: () => {
      addToast({ message: <>Workflow created successfully</>, variant: 'success' })
      onCloseDrawer()
    },
    onError: showCreateWorkflowError,
  })

  const showDeleteWorkflowError = useShowMutationError({
    prefix: 'Failed to delete workflow',
    overrides: DELETE_WORKFLOW_ERROR_OVERRIDES,
  })
  const deleteWorkflowMutation = useDeleteWorkflow({
    onSuccess: () => {
      addToast({ message: <>Workflow deleted successfully</>, variant: 'success' })
      setSelectedWorkflow(undefined)
      if (drawerContent === 'edit') {
        onSetDrawerContent('manage')
      }
    },
    onError: showDeleteWorkflowError,
  })

  const showUpdateWorkflowError = useShowMutationError({
    prefix: 'Failed to update workflow',
    overrides: UPDATE_WORKFLOW_ERROR_OVERRIDES,
  })
  const updateWorkflowMutation = usePutUpdateWorkflow({
    onSuccess: () => {
      addToast({ message: <>Workflow updated successfully</>, variant: 'success' })
      setSelectedWorkflow(undefined)
      onSetDrawerContent('manage')
    },
    onError: showUpdateWorkflowError,
  })

  const handleCreateWorkflow = (params: WorkflowParams) => {
    createWorkflowMutation.mutate({ repositoryId, workflow: toCreateWorkflowRequest(params) })
  }

  const handleEditWorkflow = (workflow: WorkflowResponse) => {
    setSelectedWorkflow(workflow)
    onSetDrawerContent('edit')
  }

  const handleUpdateWorkflow = (workflowId: string, update: UpdateWorkflowRequest) => {
    updateWorkflowMutation.mutate({ workflowId, update })
  }

  // Fall back to the list, which every user may read, instead of stranding a non-admin in a form
  // whose Save and Delete have just vanished.
  useOnDemotion({
    isWriteFormOpen: drawerContent !== 'manage' || workflowToDelete !== undefined,
    onDemote: () => {
      if (drawerContent !== 'manage') {
        // Say so. Leaving the create or edit form is a silent discard of whatever was typed into it —
        // there is no Save left to offer and no point routing through `AbandonWorkflowDialog`, since
        // the changes cannot be kept either way. Without this the form simply disappears, which reads
        // as a bug rather than a permission change.
        addToast({
          message: <>Your permissions changed. Returning to the workflow list.</>,
          variant: 'error',
        })
        onSetDrawerContent('manage')
      }
      // The confirmation renders outside the drawer-content switch, so returning to the list does not
      // dismiss it. Left standing, its Confirm fires a DELETE that can only 403.
      setWorkflowToDelete(undefined)
    },
  })

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCloseDrawer()
      }
    },
    [onCloseDrawer]
  )

  return (
    <>
      <Dialog.Root open={true} modal={false} onOpenChange={handleOpenChange}>
        {match(drawerContent)
          .with('create', () => (
            <CreateWorkflowDrawer
              repositoryType={repositoryType}
              repositoryDisplayName={repositoryDisplayName}
              hasWriteAccess={hasWriteAccess}
              onSetDrawerContent={onSetDrawerContent}
              onCloseDrawer={onCloseDrawer}
              onCreateWorkflow={handleCreateWorkflow}
            />
          ))
          .with('edit', () =>
            selectedWorkflow ? (
              <EditWorkflowDrawer
                workflow={selectedWorkflow}
                repositoryType={repositoryType}
                repositoryDisplayName={repositoryDisplayName}
                hasWriteAccess={hasWriteAccess}
                onSetDrawerContent={onSetDrawerContent}
                onUpdateWorkflow={handleUpdateWorkflow}
                onRequestDelete={() => setWorkflowToDelete(selectedWorkflow)}
              />
            ) : null
          )
          .with('manage', () => (
            <ManageWorkflowDrawer
              repositoryId={repositoryId}
              repositoryType={repositoryType}
              repositoryDisplayName={repositoryDisplayName}
              isAdmin={isAdmin}
              isRoleUnavailable={isRoleUnavailable}
              hasWriteAccess={hasWriteAccess}
              onClose={onCloseDrawer}
              onCreateNew={() => onSetDrawerContent('create')}
              onEditWorkflow={handleEditWorkflow}
            />
          ))
          .exhaustive()}
      </Dialog.Root>

      <WorkflowDeleteDialog
        workflow={workflowToDelete}
        repositoryType={repositoryType}
        repositoryDisplayName={repositoryDisplayName}
        onCancel={() => setWorkflowToDelete(undefined)}
        onConfirm={workflowId => {
          deleteWorkflowMutation.mutate({ workflowId })
          setWorkflowToDelete(undefined)
        }}
      />
    </>
  )
}

type CreateWorkflowViewProps = {
  repositoryType: Repository['type']
  repositoryDisplayName: string
  scaleItems: Scale[]
}

const CreateWorkflowView: React.FC<CreateWorkflowViewProps> = ({
  repositoryType,
  repositoryDisplayName,
  scaleItems,
}) => {
  return (
    <>
      <Dialog.Title className={`${styles.title} ${localStyles.title}`}>
        <div className={localStyles.createTitleRepo}>
          <SCMIcon type={repositoryType} size="medium" />
          <div className={localStyles.repoName}>{repositoryDisplayName}</div>
        </div>
        New workflow
      </Dialog.Title>
      <Dialog.Description className={`${styles.description} ${localStyles.separator}`}>
        Configure events and scope that result in designated actions
      </Dialog.Description>
      <CreateWorkflowForm scaleItems={scaleItems} />
    </>
  )
}

type CreateWorkflowFooterProps = {
  scaleItems: Scale[]
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onManageWorkflowsDirty: () => unknown
  onManageWorkflowsClean: () => unknown
  onCancel: () => unknown
  onCreateWorkflow: (params: WorkflowParams) => unknown
}

const CreateWorkflowFooter: React.FC<CreateWorkflowFooterProps> = ({
  scaleItems,
  hasWriteAccess,
  onManageWorkflowsDirty,
  onManageWorkflowsClean,
  onCancel,
  onCreateWorkflow,
}) => {
  return (
    <>
      <ManageWorkflowsFooterButton onDirtyClick={onManageWorkflowsDirty} onCleanClick={onManageWorkflowsClean}>
        <LightningIcon className={localStyles.workflowIcon} size={16} weight="fill" />
        <span>Manage workflows</span>
      </ManageWorkflowsFooterButton>
      <div className={localStyles.actionButtons}>
        <GhostButton type="secondary" onClick={onCancel}>
          Cancel
        </GhostButton>
        {hasWriteAccess && <CreateWorkflowButton scaleItems={scaleItems} onCreateWorkflow={onCreateWorkflow} />}
      </div>
    </>
  )
}

type EditWorkflowFooterProps = {
  workflow: WorkflowResponse
  isUpdating: boolean
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onDelete: () => unknown
  onCancel: () => unknown
  onSave: (update: UpdateWorkflowRequest) => unknown
}

const EditWorkflowFooter: React.FC<EditWorkflowFooterProps> = ({
  workflow,
  isUpdating,
  hasWriteAccess,
  onDelete,
  onCancel,
  onSave,
}) => {
  return (
    <>
      {/* Belt and braces, and the create footer does the same thing for the same reason. A non-admin
          cannot open either form — the controls that reach them are hidden and a demotion sends the
          user back to the list — but a demotion arriving mid-edit renders once before that effect
          fires, and Save, Delete and Create are the controls that would 403.

          Gated on the same `hasWriteAccess` as the controls that open these forms, so a user who
          was invited in is never stranded in a form whose Save has vanished. Cancel is ungated
          either way, so there is always a way out. */}
      <div className={localStyles.editFooterLeft}>
        {hasWriteAccess && (
          <GhostButton type="destructive" icon={<TrashSimpleIcon />} onClick={onDelete}>
            Delete workflow
          </GhostButton>
        )}
      </div>
      <div className={localStyles.editFooterRight}>
        <GhostButton type="secondary" onClick={onCancel}>
          Cancel
        </GhostButton>
        {hasWriteAccess && <EditWorkflowSaveButton workflow={workflow} isUpdating={isUpdating} onSave={onSave} />}
      </div>
    </>
  )
}

type ManageWorkflowsViewProps = {
  repositoryType: Repository['type']
  repositoryDisplayName: string
  workflows: WorkflowResponse[]
  isWorkflowsLoading: boolean
  /** `undefined` until the role is known — see `useIsAdmin`. */
  isAdmin: boolean | undefined
  isRoleUnavailable: boolean
  /** Whether to offer the write controls at all — see `useAdminRole`. */
  hasWriteAccess: boolean
  onClose: () => unknown
  onCreateNew: () => unknown
  onEditWorkflow: (workflow: WorkflowResponse) => unknown
}

const ManageWorkflowsView: React.FC<ManageWorkflowsViewProps> = ({
  repositoryType,
  repositoryDisplayName,
  workflows,
  isWorkflowsLoading,
  isAdmin,
  isRoleUnavailable,
  hasWriteAccess,
  onClose,
  onCreateNew,
  onEditWorkflow,
}) => {
  return (
    <>
      <Dialog.Close asChild>
        <button className={localStyles.closeButton} onClick={onClose} aria-label="Close dialog">
          <XIcon size={24} weight="light" />
        </button>
      </Dialog.Close>
      <div className={localStyles.manageWorkflowsContent}>
        <div className={localStyles.manageWorkflowsHeader}>
          <RepoInfo repositoryType={repositoryType} repositoryDisplayName={repositoryDisplayName} />
          <Dialog.Title className={classNames(typography.h5, localStyles.manageWorkflowsTitle)}>
            Manage workflows
          </Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description>Manage your existing workflows</Dialog.Description>
          </VisuallyHidden>
          {/* Creating, editing and deleting a workflow are admin-only on the platform (ISS-8480);
              listing them is not. So the list below stays whole for everyone and only the controls
              go. `undefined` is the answer not having arrived and renders nothing rather than the
              message — guessing wrong would tell an admin they lack a permission they hold. */}
          <div className={localStyles.manageWorkflowsActions}>
            {hasWriteAccess && (
              <GhostButton type="default" onClick={onCreateNew}>
                <PlusIcon className={localStyles.createNewIcon} size={16} weight="light" />
                Create new
              </GhostButton>
            )}

            <PermissionNotice isAdmin={isAdmin} isRoleUnavailable={isRoleUnavailable}>
              Only administrators can create or edit workflows.
            </PermissionNotice>
          </div>
        </div>
        {match({ isWorkflowsLoading, hasWorkflows: workflows.length > 0 })
          .with({ isWorkflowsLoading: true }, () => (
            <div className={sharedStyles.dialogSubtitle}>
              <Spinner label="Loading workflows" size="64" />
            </div>
          ))
          .with({ hasWorkflows: true }, () =>
            WORKFLOW_GROUP_ORDER.map(eventType => {
              const groupItems = workflows.filter(workflow => workflow.event.type === eventType)
              if (groupItems.length === 0) {
                return (
                  <div key={eventType} className={localStyles.emptyGroupHeading}>
                    {WORKFLOW_GROUP_HEADINGS[eventType]}{' '}
                    <span className={localStyles.emptyGroupCount}>({groupItems.length})</span>
                  </div>
                )
              }
              return (
                <Collapsible.Root key={eventType} defaultOpen>
                  <div className={localStyles.collapsibleHeader}>
                    <Collapsible.Trigger asChild>
                      <button className={localStyles.collapsibleTrigger}>
                        <div className={localStyles.scheduledFixesHeading}>
                          {WORKFLOW_GROUP_HEADINGS[eventType]}{' '}
                          <span className={localStyles.scheduledFixesCount}>({groupItems.length})</span>
                        </div>
                        <CaretDownIcon className={localStyles.collapsibleCaret} size={24} weight="light" />
                      </button>
                    </Collapsible.Trigger>
                  </div>
                  <Collapsible.Content>
                    <ul className={localStyles.collapsibleContent}>
                      {groupItems.map(workflow => (
                        <WorkflowItem
                          key={workflow.id}
                          workflow={workflow}
                          onEdit={hasWriteAccess ? onEditWorkflow : undefined}
                        />
                      ))}
                    </ul>
                  </Collapsible.Content>
                </Collapsible.Root>
              )
            })
          )
          .otherwise(() => (
            <div className={localStyles.emptyState}>
              <div className={localStyles.emptyStateHeadline}>No workflows</div>
              {/* Without the guard this invites a non-admin to do the thing the notice above just
                  told them they cannot, since both render for an empty repository. */}
              {hasWriteAccess && (
                <div className={localStyles.emptyStateDescription}>Create a new automation for this repository</div>
              )}
            </div>
          ))}
      </div>
    </>
  )
}

type RepoInfoProps = {
  repositoryType: Repository['type']
  repositoryDisplayName: string
}

const RepoInfo = ({ repositoryType, repositoryDisplayName }: RepoInfoProps) => (
  <div className={sharedStyles.dialogRepoInfo}>
    <SCMIcon type={repositoryType} size="medium" />
    <span>{repositoryDisplayName}</span>
  </div>
)

type WorkflowItemProps = {
  workflow: WorkflowResponse
  /** Omitted when the viewer may not edit, so the affordance follows the capability. */
  onEdit?: (workflow: WorkflowResponse) => unknown
}

const WorkflowItem = ({ workflow, onEdit }: WorkflowItemProps) => {
  const displayName = workflow.name || 'Unnamed workflow'
  return (
    <li className={localStyles.workflowItem}>
      <div className={localStyles.workflowItemRow}>
        <div className={localStyles.workflowItemBody}>
          <div className={localStyles.workflowItemTitleRow}>
            <div
              className={classNames(
                localStyles.workflowItemTitle,
                !workflow.enabled && localStyles.workflowItemTitleDisabled
              )}
            >
              {displayName}
            </div>
            {!workflow.enabled && (
              <TooltipWithHover
                size="small"
                trigger={
                  <span className={localStyles.disabledIcon} aria-label="Workflow disabled">
                    <LightningSlashIcon size={16} weight="light" />
                  </span>
                }
              >
                <span>Workflow disabled</span>
              </TooltipWithHover>
            )}
          </div>
          {workflow.created_at && (
            <div className={localStyles.workflowItemSubtitle}>
              Created {formatDate({ date: workflow.created_at, type: 'mm/dd/yyyy' })}
            </div>
          )}
        </div>
        <div className={localStyles.workflowItemActions}>
          {onEdit && (
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={localStyles.editButton}
                  onClick={() => onEdit(workflow)}
                  aria-label={`Edit workflow ${displayName}`}
                >
                  <PencilSimpleIcon className={localStyles.editIcon} />
                </button>
              }
            >
              <span>Edit workflow</span>
            </TooltipWithHover>
          )}
        </div>
      </div>
    </li>
  )
}

type WorkflowDeleteDialogProps = {
  workflow: WorkflowResponse | undefined
  repositoryType: Repository['type']
  repositoryDisplayName: string
  onCancel: () => unknown
  onConfirm: (workflowId: string) => unknown
}

const WorkflowDeleteDialog: React.FC<WorkflowDeleteDialogProps> = ({
  workflow,
  repositoryType,
  repositoryDisplayName,
  onCancel,
  onConfirm,
}) => {
  return (
    <Dialog.Root open={!!workflow} onOpenChange={() => onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className={sharedStyles.dialogOverlay} />
        <Dialog.Content className={sharedStyles.dialogContent}>
          <Dialog.Title className={sharedStyles.dialogTitle}>
            <WarningIcon className={sharedStyles.warningIcon} />
            Delete workflow?
          </Dialog.Title>
          <Dialog.Description className={sharedStyles.dialogMiddle}>
            <div className={sharedStyles.dialogMessage}>You are about to delete the following workflow:</div>
            <div className={sharedStyles.dialogSubtitle}>
              <RepoInfo repositoryType={repositoryType} repositoryDisplayName={repositoryDisplayName} />
              <div className={sharedStyles.workflowName}>{workflow?.name || 'Unnamed workflow'}</div>
            </div>
            <div className={sharedStyles.dialogMessage}>This action cannot be undone. Do you wish to proceed?</div>
          </Dialog.Description>
          <div className={sharedStyles.dialogButtons}>
            <GhostButton type="secondary" onClick={onCancel}>
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              onClick={() => {
                if (workflow) {
                  onConfirm(workflow.id)
                }
              }}
            >
              Delete workflow
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
